import { useEffect, useState } from "react";
import { ArrowUpRight, Star } from "lucide-react";

const REVIEWS_URL = "https://space.artpart.at/api/spaces/artpart/google-reviews?limit=3";

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("de-AT", {
    month: "long",
    year: "numeric",
  }).format(date);
};

const Stars = ({ rating }) => {
  const rounded = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
  return (
    <span className="flex gap-0.5" aria-label={`${rounded} von 5 Sternen`}>
      {Array.from({ length: 5 }, (_, index) => (
        <Star
          key={index}
          size={14}
          aria-hidden="true"
          className={index < rounded ? "fill-signal text-signal" : "text-ink/20"}
        />
      ))}
    </span>
  );
};

export const GoogleReviews = () => {
  const [feed, setFeed] = useState(null);
  const [expandedReviews, setExpandedReviews] = useState([]);

  useEffect(() => {
    const controller = new AbortController();

    fetch(REVIEWS_URL, {
      signal: controller.signal,
      credentials: "omit",
      headers: { Accept: "application/json" },
    })
      .then((response) => {
        if (!response.ok) throw new Error("reviews unavailable");
        return response.json();
      })
      .then((data) => {
        if (Array.isArray(data?.reviews) && data.reviews.length) setFeed(data);
      })
      .catch((error) => {
        if (error.name !== "AbortError") setFeed(null);
      });

    return () => controller.abort();
  }, []);

  if (!feed?.reviews?.length) return null;

  const rating = Number(feed.average_rating);
  const reviewCount = Number(feed.total_review_count);

  return (
    <section
      data-testid="google-reviews-section"
      aria-labelledby="google-reviews-title"
      className="relative overflow-hidden border-b border-paper-line bg-paper-surface/45 py-12 sm:py-16"
    >
      <div className="pointer-events-none absolute inset-0 ap-grid-bg opacity-25" aria-hidden="true" />
      <div className="ap-container relative">
        <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-end">
          <div>
            <p className="ap-stamp">AP · KUNDENSTIMMEN</p>
            <h2 id="google-reviews-title" className="ap-h2 mt-3 text-ink">
              WAS KUND:INNEN SAGEN.
            </h2>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-l-2 border-signal pl-4">
            {Number.isFinite(rating) ? (
              <>
                <span className="font-display text-3xl font-black leading-none text-ink">
                  {rating.toFixed(1).replace(".", ",")}
                </span>
                <Stars rating={rating} />
              </>
            ) : null}
            <span className="ap-stamp">
              {Number.isFinite(reviewCount) ? `${reviewCount} Rezensionen · ` : ""}
              Google
            </span>
          </div>
        </div>

        <div className="-mx-5 mt-8 flex snap-x snap-mandatory gap-3 overflow-x-auto px-5 pb-3 sm:-mx-8 sm:px-8 lg:mx-0 lg:grid lg:grid-cols-3 lg:overflow-visible lg:px-0 lg:pb-0">
          {feed.reviews.map((review) => (
            <article
              key={review.external_review_id}
              className="flex min-w-[84%] snap-start flex-col border border-paper-line bg-paper/75 p-5 sm:min-w-[48%] lg:min-w-0 lg:p-6"
            >
              <div className="flex items-center justify-between gap-3">
                <Stars rating={review.rating} />
                <span className="ap-stamp whitespace-nowrap">{formatDate(review.published_at)}</span>
              </div>
              <blockquote
                className={`mt-5 whitespace-pre-line text-sm leading-relaxed text-charcoal transition-[max-height] sm:text-base ${
                  expandedReviews.includes(review.external_review_id)
                    ? "max-h-[80rem]"
                    : "max-h-40 overflow-hidden"
                }`}
              >
                „{review.text}“
              </blockquote>
              {review.text?.length > 260 ? (
                <button
                  type="button"
                  onClick={() =>
                    setExpandedReviews((current) =>
                      current.includes(review.external_review_id)
                        ? current.filter((id) => id !== review.external_review_id)
                        : [...current, review.external_review_id]
                    )
                  }
                  className="ap-stamp mt-3 self-start text-ink underline decoration-signal underline-offset-4 transition-colors hover:text-signal"
                  aria-expanded={expandedReviews.includes(review.external_review_id)}
                >
                  {expandedReviews.includes(review.external_review_id)
                    ? "Weniger anzeigen"
                    : "Mehr anzeigen"}
                </button>
              ) : null}
              <p className="mt-5 font-display text-sm font-bold uppercase tracking-wide text-ink">
                {review.author || "Google-Nutzer:in"}
              </p>
              <p className="ap-stamp mt-1">Originalrezension auf Google</p>
            </article>
          ))}
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-paper-line pt-5">
          <p className="max-w-xl text-xs leading-relaxed text-concrete">
            Unveränderte Rezensionen aus dem Google-Unternehmensprofil von ArtPart.
          </p>
          {feed.profile_url ? (
            <a
              href={feed.profile_url}
              target="_blank"
              rel="noopener noreferrer"
              className="ap-stamp inline-flex items-center gap-2 text-ink transition-colors hover:text-signal"
            >
              Alle Rezensionen auf Google <ArrowUpRight size={13} />
            </a>
          ) : null}
        </div>
      </div>
    </section>
  );
};
